import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Star, Heart, MapPin, BadgeCheck } from 'lucide-react';
import { Badge } from './ui/badge';
import { motion } from 'framer-motion';

const tagColors = {
  "Best Seller": "bg-amber-500 text-white",
  "Customer's Choice": "bg-pink-500 text-white",
  "Top Rated": "bg-yellow-500 text-black",
  "New": "bg-blue-500 text-white",
  "Premium": "bg-purple-500 text-white",
  "Budget Friendly": "bg-green-500 text-white",
};

const VendorCard = ({ vendor, onFavorite, isFavorited = false }) => {
  const [favorite, setFavorite] = useState(isFavorited);
  const [imageIndex, setImageIndex] = useState(0);

  const handleFavorite = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setFavorite(!favorite);
    onFavorite?.(vendor.id, !favorite);
  };

  const images = vendor.images?.length > 0 
    ? vendor.images 
    : ['https://images.unsplash.com/photo-1519741497674-611481863552?w=800'];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="group"
      data-testid={`vendor-card-${vendor.id}`}
    >
      <Link to={`/vendors/${vendor.id}`} className="block" data-testid={`vendor-link-${vendor.id}`}>
        <div className="bg-white rounded-2xl overflow-hidden border-2 border-cream-200 hover:border-gold-400 transition-all hover:shadow-hover card-hover">
          {/* Image */}
          <div className="relative aspect-[4/3] overflow-hidden">
            <img
              src={images[imageIndex]}
              alt={vendor.business_name}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
            
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-maroon-900/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            
            {/* Favorite Button */}
            <button
              onClick={handleFavorite}
              className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110 z-10 shadow-md"
              data-testid={`favorite-btn-${vendor.id}`}
            >
              <Heart 
                className={`w-5 h-5 transition-colors ${
                  favorite ? 'text-maroon-600 fill-maroon-600' : 'text-maroon-400'
                }`} 
              />
            </button>

            {/* Vendor Tags - Top Left */}
            <div className="absolute top-3 left-3 flex flex-col gap-1.5 z-10">
              {vendor.is_featured && (
                <Badge className="bg-gradient-to-r from-gold-500 to-gold-600 text-maroon-900 border-0 font-semibold shadow-gold">
                  ✦ Featured
                </Badge>
              )}
              {/* Show first 2 tags */}
              {(vendor.tags || []).slice(0, 2).map((tag) => (
                <Badge key={tag} className={`${tagColors[tag] || 'bg-gray-500 text-white'} border-0 text-xs font-semibold shadow-sm`}>
                  {tag}
                </Badge>
              ))}
            </div>

            {/* Image Dots */}
            {images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                {images.slice(0, 4).map((img, idx) => (
                  <button
                    key={`dot-${idx}-${img?.slice(-8) || idx}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setImageIndex(idx);
                    }}
                    className={`w-2 h-2 rounded-full transition-all ${
                      idx === imageIndex ? 'bg-gold-400 w-4' : 'bg-white/70'
                    }`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="p-5">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div className="flex-1 min-w-0">
                <h3 className="font-heading font-bold text-lg text-maroon-900 truncate flex items-center gap-2">
                  {vendor.business_name}
                  {vendor.is_verified && (
                    <BadgeCheck className="w-5 h-5 text-gold-500 flex-shrink-0" />
                  )}
                </h3>
                <div className="flex items-center gap-1.5 text-maroon-500 text-sm mt-1">
                  <MapPin className="w-4 h-4" />
                  <span>{vendor.cities?.length > 0 ? vendor.cities.join(', ') : vendor.city}</span>
                </div>
              </div>
              
              {/* Rating */}
              {vendor.rating > 0 && (
                <div className="flex items-center gap-1.5 bg-gold-50 px-2.5 py-1 rounded-full">
                  <Star className="w-4 h-4 text-gold-500 fill-gold-500" />
                  <span className="text-sm font-bold text-maroon-800">{vendor.rating?.toFixed(1)}</span>
                </div>
              )}
            </div>

            {/* Category */}
            <Badge variant="secondary" className="bg-maroon-50 text-maroon-700 hover:bg-maroon-100 capitalize text-xs mb-4 font-medium">
              {vendor.category?.replace('-', ' ')}
            </Badge>

            {/* Price & Reviews */}
            <div className="flex items-center justify-between pt-4 border-t border-cream-200">
              <div>
                <span className="text-xs text-maroon-400">Starting from</span>
                <p className="text-lg font-heading font-bold text-maroon-900">
                  ${vendor.price_range_min?.toLocaleString() || '0'}
                </p>
              </div>
              <span className="text-sm text-maroon-400">
                {vendor.review_count || 0} reviews
              </span>
            </div>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default VendorCard;
